import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAnchoredMenu } from './useAnchoredMenu';

export { anchorRect } from './useAnchoredMenu';

// Shared row-action dropdown — the single canonical implementation used by
// every Core Module's table (Livestock Population, Market & Trade, Infrastructure,
// Conflicts & Disputes, Workforce & Institutional, Planning & Reporting):
// a floating menu (status pill + View Details / Edit Record / workflow quick
// actions / Reject (with required notes) / Delete) triggered by a table row's
// three-dot button. Each table passes its own role-capability matrix and
// status-transition map so the same component drives every module's action
// column consistently.

// Standard role-capability matrix — the same shape/values used across
// Population, Border Crossing and Vet Certificate's ActionDropdowns. Use this
// unless a module has a documented, different RBAC rule for that resource.
export const STANDARD_ROLE_CAN = {
  R01: { edit: true,  submit: true,  validate: true,  approve: true,  reject: true,  delete: true  },
  R02: { edit: false, submit: false, validate: false, approve: true,  reject: true,  delete: false },
  R03: { edit: false, submit: false, validate: true,  approve: false, reject: true,  delete: false },
  R04: { edit: true,  submit: true,  validate: true,  approve: false, reject: true,  delete: false },
  R05: { edit: true,  submit: true,  validate: false, approve: false, reject: false, delete: false },
  R06: { edit: false, submit: false, validate: false, approve: false, reject: false, delete: false },
};

// ── Livestock Population's action rules, for every module ──────────────────
// The status gates below are exactly PopulationDataTable's getMenuItems(), and
// match the backend for every workflow resource (ValidationWorkflowService +
// each *Policy.php):
//   • Edit Record      — role may edit, status Draft or Rejected
//   • Submit for Review — Draft (R05 also re-submits a Rejected record)
//   • Validate          — Submitted            (R01, R03, R04)
//   • Approve           — Validated            (R01, R02)
//   • Reject            — Submitted/Validated  (R02: Validated only)
//   • Delete Draft      — R01, Draft only
// So an Approved record shows only "View Details", as on Livestock.
// What differs per resource is WHICH roles may edit / submit (and, for a few
// resources, validate / reject) — pass those from that resource's backend
// Policy so no action is offered that the backend would refuse.
const LIVESTOCK_VALIDATORS = ['R01', 'R03', 'R04'];
const LIVESTOCK_APPROVERS = ['R01', 'R02'];
const LIVESTOCK_REJECTERS = ['R01', 'R02', 'R03', 'R04'];
const ALL_ROLES = ['R01', 'R02', 'R03', 'R04', 'R05', 'R06'];

export function buildWorkflowRules({
  editors,
  submitters = editors,
  validators = LIVESTOCK_VALIDATORS,
  approvers = LIVESTOCK_APPROVERS,
  rejecters = LIVESTOCK_REJECTERS,
}) {
  const roleCan = {};
  const transitions = {};
  const editableStatuses = {};

  ALL_ROLES.forEach((roleId) => {
    const can = {
      edit: editors.includes(roleId),
      submit: submitters.includes(roleId),
      validate: validators.includes(roleId),
      approve: approvers.includes(roleId),
      reject: rejecters.includes(roleId),
      delete: roleId === 'R01',
    };
    roleCan[roleId] = can;
    editableStatuses[roleId] = can.edit ? ['draft', 'rejected'] : [];
    transitions[roleId] = {
      draft: can.submit ? ['submitted'] : [],
      rejected: can.submit && roleId === 'R05' ? ['submitted'] : [],
      submitted: [
        ...(can.validate ? ['validated'] : []),
        ...(can.reject && roleId !== 'R02' ? ['rejected'] : []),
      ],
      validated: [
        ...(can.approve ? ['approved'] : []),
        ...(can.reject ? ['rejected'] : []),
      ],
      approved: [],
    };
  });

  return { roleCan, transitions, editableStatuses, deleteStatuses: ['draft'] };
}

// Per-resource role lists, from the backend Policies
// (NlimsBackend/fmld-nlims-backend/app/Policies/*Policy.php).
export const WORKFLOW_RULES = {
  population:        buildWorkflowRules({ editors: ['R01', 'R04', 'R05'] }),
  conflictRecords:   buildWorkflowRules({ editors: ['R01', 'R04'], submitters: ['R01', 'R04', 'R05'] }),
  conflictCommittees: buildWorkflowRules({ editors: ['R01', 'R04'], submitters: ['R01', 'R04', 'R05'] }),
  transhumanceRoutes: buildWorkflowRules({ editors: ['R01', 'R04'], submitters: ['R01', 'R04', 'R05'] }),
  markets:           buildWorkflowRules({ editors: ['R01', 'R04'] }),
  marketPrices:      buildWorkflowRules({ editors: ['R01', 'R04', 'R05'] }),
  borderCrossings:   buildWorkflowRules({ editors: ['R01', 'R03', 'R04'] }),
  // Vet certificates are R03's instrument; R04 has no rights on them.
  vetCertificates:   buildWorkflowRules({ editors: ['R01', 'R03'], validators: ['R01', 'R03'], rejecters: ['R01', 'R02', 'R03'] }),
  // R05 (Field Officer) registers facilities in their LGA, like R04 in their State.
  facilities:        buildWorkflowRules({ editors: ['R01', 'R02', 'R04', 'R05'] }),
  // DCIC-07 (v2.2): status updates R05 → R04; climate assessments are created
  // by R04/R03 and approved by R02. R05 never sees climate assessments.
  facilityStatusUpdates: buildWorkflowRules({ editors: ['R01', 'R03', 'R04', 'R05'] }),
  livestockActors:   buildWorkflowRules({ editors: ['R01', 'R02', 'R04'] }),
  workforceDeployments: buildWorkflowRules({ editors: ['R01', 'R02', 'R04'] }),
  cooperatives:      buildWorkflowRules({ editors: ['R01', 'R04'], submitters: ['R01', 'R04', 'R05'] }),
  workplans:         buildWorkflowRules({ editors: ['R01', 'R02', 'R04'] }),
  activityReports:   buildWorkflowRules({ editors: ['R01', 'R02', 'R04'] }),
  // No backend endpoint yet (Feed Production, PPP) — same shape as the
  // other Infrastructure / Planning resources.
  feedProduction:    buildWorkflowRules({ editors: ['R01', 'R02', 'R04'] }),
  pppArrangements:   buildWorkflowRules({ editors: ['R01', 'R02', 'R04'] }),
};

// ── Record Status pipeline (edit page) ──────────────────────────────────────
// Which statuses a role can move a record to from the edit page, and the
// backend steps each one runs automatically. Forward stages chain for as long
// as the role holds each step's right (e.g. R01 on a Draft → Submitted,
// Validated or Approved; choosing Approved runs submit → validate → approve).
// Reject is always a single step, from Submitted/Validated (R02: Validated).
// A Rejected record restarts from Draft — the backend's submit accepts both.
export const PIPELINE_STAGES = ['draft', 'submitted', 'validated', 'approved'];
const STAGE_RIGHT = { submitted: 'submit', validated: 'validate', approved: 'approve' };
const statusLabel = (status) => status.charAt(0).toUpperCase() + status.slice(1);

export function getStatusPipelineTargets(rules, roleId, currentStatus) {
  const can = rules?.roleCan?.[roleId] ?? {};
  const current = String(currentStatus || 'draft').toLowerCase();
  const targets = [];

  const startIndex = current === 'rejected' ? 0 : PIPELINE_STAGES.indexOf(current);
  if (startIndex >= 0) {
    const path = [];
    for (let i = startIndex + 1; i < PIPELINE_STAGES.length; i += 1) {
      const stage = PIPELINE_STAGES[i];
      if (!can[STAGE_RIGHT[stage]]) break;
      path.push(stage);
      targets.push({ value: stage, label: statusLabel(stage), path: [...path] });
    }
  }

  const canReject = can.reject && (roleId === 'R02' ? current === 'validated' : ['submitted', 'validated'].includes(current));
  if (canReject) targets.push({ value: 'rejected', label: 'Rejected', path: ['rejected'] });

  return targets;
}

const DEFAULT_STATUS_PILL = {
  draft:     { bg: 'bg-gray-100',   text: 'text-gray-600',   label: 'Draft' },
  submitted: { bg: 'bg-blue-50',    text: 'text-blue-700',   label: 'Submitted' },
  validated: { bg: 'bg-purple-50',  text: 'text-purple-700', label: 'Validated' },
  approved:  { bg: 'bg-green-50',   text: 'text-green-700',  label: 'Approved' },
  rejected:  { bg: 'bg-red-50',     text: 'text-red-700',    label: 'Rejected' },
};

export function StatusPill({ status, styles = DEFAULT_STATUS_PILL, size = 'md' }) {
  const key = String(status ?? 'draft').toLowerCase();
  const s = styles[key] ?? styles.draft ?? { bg: 'bg-gray-100', text: 'text-gray-600', label: status || 'Draft' };
  const sizeCls = size === 'sm' ? 'text-[11px] px-2 py-0.5' : 'text-xs px-2.5 py-1';
  return <span className={`font-semibold rounded-full ${sizeCls} ${s.bg} ${s.text}`}>{s.label}</span>;
}

// Resolves which menu items should show for a given row, based on a
// { R01: { edit, submit, validate, approve, reject, delete }, ... } matrix
// and an optional { R01: { draft: ['submitted'], ... }, ... } transition map.
export function computeMenuFlags(row, roleId, {
  roleCan = {},
  transitions = null,
  statusField = 'data_status',
  deleteStatuses = ['draft'],
  editableStatuses = null,
} = {}) {
  // A row that carries the workflow field uses it (empty = Draft, the backend
  // default) — it must not fall through to an unrelated operational `status`
  // such as a market's or actor's Active/Inactive.
  const status = String(
    (row && Object.prototype.hasOwnProperty.call(row, statusField))
      ? (row[statusField] || 'draft')
      : (row?.status ?? row?.validation_status ?? row?.workflow_status ?? 'draft')
  ).toLowerCase();
  const rc = roleCan[roleId] ?? {};
  const nextStatuses = transitions?.[roleId]?.[status] ?? [];

  const showEdit = !!rc.edit && (!editableStatuses || (editableStatuses[roleId] ?? []).includes(status));
  const showSubmit = !!rc.submit && (transitions ? nextStatuses.includes('submitted') : status === 'draft' || status === 'rejected');
  const showValidate = !!rc.validate && (transitions ? nextStatuses.includes('validated') : status === 'submitted');
  const showApprove = !!rc.approve && (transitions ? nextStatuses.includes('approved') : status === 'validated');
  const showReject = !!rc.reject && (transitions ? nextStatuses.includes('rejected') : ['submitted', 'validated'].includes(status));
  const showDelete = !!rc.delete && deleteStatuses.includes(status);

  return { status, showEdit, showSubmit, showValidate, showApprove, showReject, showDelete };
}

function MenuItem({ onClick, colorCls, icon, label, dividerBefore }) {
  return (
    <>
      {dividerBefore && <div className="my-1 h-px bg-gray-100 mx-3" />}
      <button
        type="button"
        onClick={onClick}
        className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-left whitespace-nowrap transition-colors ${colorCls}`}
      >
        <span className="w-4 flex justify-center flex-shrink-0">{icon}</span>
        {label}
      </button>
    </>
  );
}

const ICONS = {
  view: <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>,
  edit: <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>,
  submit: <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" /></svg>,
  validate: <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  approve: <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>,
  reject: <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  delete: <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>,
};

export function ActionDropdown({
  row, rect, roleId, onClose,
  onView, onEdit, onSubmit, onValidate, onApprove, onReject, onDelete,
  roleCan, transitions, statusField, deleteStatuses, editableStatuses,
  rules,
  statusStyles,
  deleteLabel = 'Delete Draft',
}) {
  // `rules` (from WORKFLOW_RULES) supplies the Livestock-style matrix in one
  // prop; individually passed props still win.
  const { status, showEdit, showSubmit, showValidate, showApprove, showReject, showDelete } = computeMenuFlags(row, roleId, {
    roleCan: roleCan ?? rules?.roleCan,
    transitions: transitions ?? rules?.transitions,
    statusField,
    deleteStatuses: deleteStatuses ?? rules?.deleteStatuses,
    editableStatuses: editableStatuses ?? rules?.editableStatuses,
  });
  // Always opens directly below the button and follows it on scroll.
  const { menuRef, style: anchoredStyle } = useAnchoredMenu(rect, onClose);
  const style = rect ? { ...anchoredStyle, minWidth: 220 } : { display: 'none' };

  const act = (fn) => () => { fn?.(); onClose(); };
  const workflowItems = showSubmit || showValidate || showApprove;

  // Portal to <body>: a transformed/filtered ancestor (page transitions,
  // card effects) would otherwise make `position: fixed` relative to that
  // ancestor and throw the menu away from its button.
  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998]" onClick={onClose} />
      <div ref={menuRef} role="menu" style={style} className="rounded-xl bg-white shadow-2xl border border-gray-100 py-1.5 overflow-x-hidden">
        <div className="px-4 pb-2 pt-1 border-b border-gray-50 flex items-center gap-2">
          <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Status</span>
          <StatusPill status={status} styles={statusStyles} />
        </div>

        <MenuItem onClick={act(onView)} colorCls="text-gray-700 hover:bg-gray-50" icon={ICONS.view} label="View Details" />

        {showEdit && (
          <MenuItem onClick={act(onEdit)} colorCls="text-gray-700 hover:bg-gray-50" icon={ICONS.edit} label="Edit Record" />
        )}

        {workflowItems && (
          <>
            <div className="my-1 h-px bg-gray-100 mx-3" />
            {showSubmit && (
              <MenuItem onClick={act(onSubmit)} colorCls="text-blue-700 hover:bg-blue-50" icon={ICONS.submit} label="Submit for Review" />
            )}
            {showValidate && (
              <MenuItem onClick={act(onValidate)} colorCls="text-amber-700 hover:bg-amber-50" icon={ICONS.validate} label="Validate" />
            )}
            {showApprove && (
              <MenuItem onClick={act(onApprove)} colorCls="text-green-700 hover:bg-green-50" icon={ICONS.approve} label="Approve" />
            )}
          </>
        )}

        {showReject && (
          <MenuItem onClick={act(onReject)} colorCls="text-red-600 hover:bg-red-50" dividerBefore icon={ICONS.reject} label="Reject" />
        )}

        {showDelete && (
          <MenuItem onClick={act(onDelete)} colorCls="text-red-600 hover:bg-red-50" dividerBefore={!showReject} icon={ICONS.delete} label={deleteLabel} />
        )}
      </div>
    </>,
    document.body
  );
}

export function QuickConfirmModal({ title, message, onConfirm, onClose, busy }) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <h3 className="text-base font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600">{message}</p>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 h-10 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={onConfirm} disabled={busy} className="flex-1 h-10 rounded-full bg-red-600 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40">
            {busy ? 'Processing…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Required-notes Reject confirmation — every workflow's Reject action routes
// through this so a reason is always captured for the originating officer,
// instead of firing the reject API call with no notes.
export function RejectNotesModal({ onConfirm, onClose }) {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const textRef = useRef(null);

  useEffect(() => { textRef.current?.focus(); }, []);

  const handleConfirm = async () => {
    if (!notes.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      await onConfirm(notes.trim());
    } catch (e) {
      setError(e?.response?.data?.message ?? e.message ?? 'Reject failed.');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1220] flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Reject Record</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              The record will return to Draft. The rejection reason will be visible to the originating officer.
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </div>
        )}

        <label className="block text-xs font-semibold text-gray-600 mb-1">
          Rejection Reason <span className="text-red-500">*</span>
        </label>
        <textarea
          ref={textRef}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          placeholder="Describe what must be corrected before the record can be resubmitted..."
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400 resize-none placeholder-gray-400"
        />
        {!notes.trim() && (
          <p className="mt-1 text-xs text-red-500">Rejection reason is required.</p>
        )}

        <div className="flex gap-3 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-10 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!notes.trim() || busy}
            className="flex-1 h-10 rounded-full bg-red-600 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? 'Rejecting...' : 'Confirm Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Named delete confirmation — pass `entityLabel` (a string, or a function
// receiving `row`) so each module can describe the record being deleted in
// its own terms (e.g. a facility name, a market name, a record's state/LGA).
export function DeleteConfirmModal({ row, entityLabel, onConfirm, onClose, busy, title = 'Delete Draft Record?', noun = 'draft record' }) {
  const label = typeof entityLabel === 'function' ? entityLabel(row) : (entityLabel ?? row?.name ?? '');
  return (
    <div className="fixed inset-0 z-[1220] flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <h3 className="text-base font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600">
          You are about to permanently delete the {noun}{label ? <> for <strong className="text-gray-900">{label}</strong></> : ''}.
          This cannot be undone.
        </p>
        <div className="flex gap-3 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-10 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="flex-1 h-10 rounded-full bg-red-600 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40"
          >
            {busy ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Busy-spinner overlay shown while a workflow action's API call is in flight.
export function QuickActionOverlay({ label }) {
  return (
    <div className="fixed inset-0 z-[1210] flex items-center justify-center bg-black/20">
      <div className="rounded-xl bg-white px-6 py-4 shadow-xl text-sm font-semibold text-gray-700 flex items-center gap-3">
        <svg className="animate-spin h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        {label}
      </div>
    </div>
  );
}

// Three-dot action-cell HTML for DataTables render functions.
export function actionCellHtml() {
  return `<button class="nlims-action-btn" data-action="menu" style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:4px 14px;cursor:pointer;color:#15803d;font-weight:800;font-size:15px;letter-spacing:2px;line-height:1.4;" title="Actions">&#8943;</button>`;
}
